import ApolloClient, { ApolloError } from 'apollo-client'
import * as React from 'react'
import { ApolloConsumer, Mutation, MutationFunction } from 'react-apollo'

import { Terms } from 'main/components/common/AuthModal'
import Form, { FormGroup, FormSection } from 'main/components/common/Form'
import { AppContext, IAppContext } from 'main/framework'
import { ExternalSchoolFields as IExternalSchool } from 'main/gql/externalSchool'
import {
  CREATE_DIRECTOR_USER_MUTATION,
  CreateDirectorUser,
  CreateDirectorUserVariables,
  User_user,
} from 'main/gql/user'
import { SLACK_NOTIFICATION_CHANNEL } from 'main/pages/ExternalSchool'
import { Button, colors, PhoneTextField, spacing, Text, TextArea, TextField, Title } from 'main/ui'
import { formatAddress } from 'main/util/address'
import { login } from 'main/util/auth'
import segment from 'util/analytics/segment'

import { Step } from '../../../types'
import { IParams, IValidation } from './types'
import { initialParams, validate } from './util'

export interface IProps {
  user: User_user | null
  externalSchool: IExternalSchool
  onChangeStep: React.Dispatch<React.SetStateAction<Step>>
}

export default function JoinForm(props: IProps) {
  const { user, externalSchool, onChangeStep } = props

  const appContext = React.useContext(AppContext)

  const [params, setParams] = React.useState<IParams>(initialParams(user))
  const [validationHints, setValidationHints] = React.useState<IValidation>({})
  const [error, setError] = React.useState<boolean>(false)

  function handleSubmit(mutate: MutationFunction<CreateDirectorUser, CreateDirectorUserVariables>) {
    setError(false)
    setValidationHints({})

    const hints = validate(params)
    if (hints) return setValidationHints(hints)

    // If a user is provided to the JoinForm (implying the submitter of the JoinForm is logged in),
    // then the CreateDirectorUserMutation is not triggered, and we skip directly to performing the
    // Segment track and changing the JoinModal's step.
    if (user) {
      performSegmentTrack(params, externalSchool)
      return onChangeStep(Step.Confirmation)
    }

    mutate({
      variables: {
        user: {
          directorTosAgreed: true,
          email: params.email.trim(),
          family: {},
          firstName: params.firstName,
          isLicensed: !!externalSchool.licenseNumber,
          lastName: params.lastName,
          phone: params.phone,
          postalCode: externalSchool.address.postalCode
            ? externalSchool.address.postalCode.trim()
            : '',
          signUpSource: 'director_external_school_join_ws_modal',
        },
      },
    })
  }

  function handleCompleted(
    { createDirectorUser }: CreateDirectorUser,
    client: ApolloClient<{}>,
    context: IAppContext,
  ) {
    const credentials = createDirectorUser && createDirectorUser.credentials

    if (!credentials) {
      throw new Error('no credentials')
    } else {
      login(client, credentials, context).then(() => {
        performSegmentTrack(params, externalSchool)
        onChangeStep(Step.ConfirmationWithPassword)
      })
    }
  }

  function handleError(apolloError: ApolloError) {
    const messages = apolloError.graphQLErrors.map(e => e.message)

    // If the CreateDirectorUserMutation results in the 'email has already been taken' error, rather
    // than surfacing an error message, we skip directly to performing the Segment track and
    // changing the JoinModal's step. This is because this specific error tells us that the
    // objective of triggering the CreateDirectorUserMutation (to create a user corresponding with
    // the submitter of the JoinForm) has already been fulfilled.
    if (messages.includes('email has already been taken')) {
      performSegmentTrack(params, externalSchool)
      return onChangeStep(Step.Confirmation)
    }

    setError(true)
  }

  return (
    <ApolloConsumer>
      {client => (
        <Mutation<CreateDirectorUser, CreateDirectorUserVariables>
          mutation={CREATE_DIRECTOR_USER_MUTATION}
          onCompleted={data => handleCompleted(data, client, appContext)}
          onError={handleError}
        >
          {(mutate, { loading }) => (
            <Form onSubmit={() => handleSubmit(mutate)}>
              <FormSection>
                <Title kind="section">Interested in joining? Let's talk.</Title>
                <FormGroup>
                  <TextField
                    disabled={loading}
                    label="First name"
                    placeholder="Enter a first name"
                    validationHint={validationHints.firstName}
                    value={params.firstName}
                    onChange={firstName => setParams({ ...params, firstName })}
                    required
                  />
                  <TextField
                    disabled={loading}
                    label="Last name"
                    placeholder="Enter a last name"
                    validationHint={validationHints.lastName}
                    value={params.lastName}
                    onChange={lastName => setParams({ ...params, lastName })}
                    required
                  />
                </FormGroup>
                <TextField
                  disabled={loading}
                  label="Email"
                  placeholder="Enter an email"
                  validationHint={validationHints.email}
                  value={params.email}
                  onChange={email => setParams({ ...params, email })}
                  required
                />
                <PhoneTextField
                  disabled={loading}
                  label="Phone number"
                  placeholder="Phone number (mobile)"
                  validationHint={validationHints.phone}
                  value={params.phone}
                  onChange={phone => setParams({ ...params, phone })}
                  required
                />
                <TextArea
                  disabled={loading}
                  placeholder="Message (optional)"
                  value={params.message || ''}
                  onChange={message => setParams({ ...params, message })}
                />
                {!user && <Terms />}
                {error && (
                  <div css={{ marginTop: spacing.majorScale(2) }}>
                    <Text css={{ color: colors.text.error }}>
                      Something went wrong! Please try again.
                    </Text>
                  </div>
                )}
                <div css={{ marginTop: spacing.majorScale(2) }}>
                  <Button full kind="regular" disabled={loading}>
                    {loading ? 'Submitting...' : 'Submit'}
                  </Button>
                </div>
              </FormSection>
            </Form>
          )}
        </Mutation>
      )}
    </ApolloConsumer>
  )
}

function performSegmentTrack(params: IParams, externalSchool: IExternalSchool) {
  const data = {
    directorEmailGiven: params.email,
    directorEmailScraped: externalSchool.directorEmail,
    directorFirstName: params.firstName,
    directorLastName: params.lastName,
    directorPhoneGiven: params.phone,
    directorPhoneScraped: externalSchool.directorPhone,
    externalSchoolLocation: formatAddress(externalSchool.address),
    message: params.message,
  }

  // Fire with a slack channel name as recipient for ops notifications.
  segment.track('Click Submit Director External School Join WS Modal Form', {
    ...data,
    recipient: SLACK_NOTIFICATION_CHANNEL,
  })

  // Fire with a provided email as recipient for auto responder email.
  segment.track('Respond Submit Director External School Join WS Modal Form', {
    ...data,
    recipient: params.email,
  })
}
